'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

export interface Stream {
  id: string;
  name: string;
  streamType: 'TV' | 'Radio' | 'Event';
  country: string;
  flag: string;
  category: string;
  language: string;
  viewers: number;
  streamStatus: 'live' | 'offline' | 'degraded' | 'draft' | 'suspended';
  streamUrl: string;
  embedCode?: string;
  thumbnail?: string;
  thumbnailAlt?: string;
  logoUrl?: string;
  description?: string;
  uptime: string;
  lastVerifiedAt?: string;
  addedBy?: string;
  isFeatured: boolean;
  genre?: string;
  nowPlaying?: string;
  listeners?: number;
  createdAt: string;
  updatedAt: string;
}

export interface LiveEvent {
  id: string;
  title: string;
  subtitle?: string;
  channel?: string;
  country: string;
  flag: string;
  eventTime: string;
  eventStatus: 'live' | 'upcoming' | 'ended';
  statusLabel?: string;
  viewers: number;
  category: string;
  thumbnail?: string;
  thumbnailAlt?: string;
  streamId?: string;
}

export interface ViewerTrend {
  time: string;
  viewers: number;
  streams: number;
}

export interface CountryViewerStat {
  country: string;
  viewers: number;
}

export interface PeakHour {
  hour: string;
  avgViewers: number;
  maxViewers: number;
}

export interface EngagementMetric {
  streamName: string;
  avgWatchTime: number;
  totalSessions: number;
  bounceRate: number;
  returnViewerRate: number;
  chatMessages: number;
  shares: number;
}

function mapStream(row: any): Stream {
  return {
    id: row.id,
    name: row.name,
    streamType: row.stream_type,
    country: row.country,
    flag: row.flag,
    category: row.category,
    language: row.language,
    viewers: row.viewers ?? 0,
    streamStatus: row.stream_status,
    streamUrl: row.stream_url,
    embedCode: row.embed_code,
    thumbnail: row.thumbnail,
    thumbnailAlt: row.thumbnail_alt,
    logoUrl: row.logo_url,
    description: row.description,
    uptime: row.uptime ?? '—',
    lastVerifiedAt: row.last_verified_at,
    addedBy: row.added_by,
    isFeatured: row.is_featured ?? false,
    genre: row.genre,
    nowPlaying: row.now_playing,
    listeners: row.listeners ?? 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapEvent(row: any): LiveEvent {
  return {
    id: row.id,
    title: row.title,
    subtitle: row.subtitle,
    channel: row.channel,
    country: row.country,
    flag: row.flag,
    eventTime: row.event_time,
    eventStatus: row.event_status,
    statusLabel: row.status_label,
    viewers: row.viewers ?? 0,
    category: row.category,
    thumbnail: row.thumbnail,
    thumbnailAlt: row.thumbnail_alt,
    streamId: row.stream_id,
  };
}

export const streamService = {
  async getTVChannels(country?: string): Promise<Stream[]> {
    const supabase = createClient();
    try {
      let query = supabase
        .from('streams')
        .select('*')
        .eq('stream_type', 'TV')
        .neq('stream_status', 'draft')
        .neq('stream_status', 'suspended')
        .order('viewers', { ascending: false });

      if (country && country !== 'all') {
        query = query.ilike('country', `%${country}%`);
      }

      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map(mapStream) ?? [];
    } catch (err: any) {
      console.error('getTVChannels error:', err.message);
      return [];
    }
  },

  async getFeaturedStream(): Promise<Stream | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('streams')
        .select('*')
        .eq('is_featured', true)
        .eq('stream_type', 'TV')
        .neq('stream_status', 'suspended')
        .limit(1)
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? mapStream(data) : null;
    } catch (err: any) {
      console.error('getFeaturedStream error:', err.message);
      return null;
    }
  },

  async getRadioStations(): Promise<Stream[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('streams')
        .select('*')
        .eq('stream_type', 'Radio')
        .neq('stream_status', 'suspended')
        .order('listeners', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map(mapStream) ?? [];
    } catch (err: any) {
      console.error('getRadioStations error:', err.message);
      return [];
    }
  },

  async getLiveEvents(): Promise<LiveEvent[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('live_events')
        .select('*')
        .in('event_status', ['live', 'upcoming'])
        .order('viewers', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map(mapEvent) ?? [];
    } catch (err: any) {
      console.error('getLiveEvents error:', err.message);
      return [];
    }
  },

  async getAllStreams(): Promise<Stream[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('streams')
        .select('*')
        .order('viewers', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map(mapStream) ?? [];
    } catch (err: any) {
      console.error('getAllStreams error:', err.message);
      return [];
    }
  },

  async getViewerTrends(): Promise<ViewerTrend[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('viewer_trends')
        .select('*')
        .eq('trend_date', new Date().toISOString().split('T')[0])
        .order('recorded_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (
        data?.map((row) => ({
          time: row.recorded_at,
          viewers: row.viewers,
          streams: row.active_streams,
        })) ?? []
      );
    } catch (err: any) {
      console.error('getViewerTrends error:', err.message);
      return [];
    }
  },

  async getCountryViewerStats(): Promise<CountryViewerStat[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('country_viewer_stats')
        .select('*')
        .eq('stat_date', new Date().toISOString().split('T')[0])
        .order('viewers', { ascending: false })
        .limit(8);

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map((row) => ({ country: row.country, viewers: row.viewers })) ?? [];
    } catch (err: any) {
      console.error('getCountryViewerStats error:', err.message);
      return [];
    }
  },

  async getPeakHours(): Promise<PeakHour[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('peak_hours')
        .select('hour_of_day, avg_viewers, max_viewers')
        .eq('stat_date', new Date().toISOString().split('T')[0])
        .order('hour_of_day', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (
        data?.map((row) => ({
          hour: `${String(row.hour_of_day).padStart(2, '0')}:00`,
          avgViewers: row.avg_viewers,
          maxViewers: row.max_viewers,
        })) ?? []
      );
    } catch (err: any) {
      console.error('getPeakHours error:', err.message);
      return [];
    }
  },

  async getEngagementMetrics(): Promise<EngagementMetric[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('engagement_metrics')
        .select('stream_name, avg_watch_time_minutes, total_sessions, bounce_rate, return_viewer_rate, chat_messages, shares')
        .eq('stat_date', new Date().toISOString().split('T')[0])
        .order('total_sessions', { ascending: false })
        .limit(8);

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (
        data?.map((row) => ({
          streamName: row.stream_name,
          avgWatchTime: Number(row.avg_watch_time_minutes),
          totalSessions: row.total_sessions,
          bounceRate: Number(row.bounce_rate),
          returnViewerRate: Number(row.return_viewer_rate),
          chatMessages: row.chat_messages,
          shares: row.shares,
        })) ?? []
      );
    } catch (err: any) {
      console.error('getEngagementMetrics error:', err.message);
      return [];
    }
  },

  async getStreamKPIs() {
    const supabase = createClient();
    try {
      const { data: allStreams, error } = await supabase
        .from('streams')
        .select('stream_type, stream_status, country');

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }

      const total = allStreams?.length ?? 0;
      const liveNow = allStreams?.filter((s) => s.stream_status === 'live').length ?? 0;
      const degraded = allStreams?.filter((s) => s.stream_status === 'degraded').length ?? 0;
      const offline = allStreams?.filter((s) => s.stream_status === 'offline').length ?? 0;
      const tvCount = allStreams?.filter((s) => s.stream_type === 'TV').length ?? 0;
      const radioCount = allStreams?.filter((s) => s.stream_type === 'Radio').length ?? 0;
      const eventCount = allStreams?.filter((s) => s.stream_type === 'Event').length ?? 0;
      const countries = new Set(allStreams?.map((s) => s.country) ?? []).size;

      return { total, liveNow, degraded, offline, tvCount, radioCount, eventCount, countries };
    } catch (err: any) {
      console.error('getStreamKPIs error:', err.message);
      return null;
    }
  },

  async getDashboardMetrics() {
    const supabase = createClient();
    try {
      const { data: streams, error: streamsError } = await supabase
        .from('streams')
        .select('stream_status, viewers, country');

      if (streamsError) {
        if (isSchemaError(streamsError)) throw streamsError;
        return null;
      }

      const activeStreams = streams?.filter((s) => s.stream_status === 'live').length ?? 0;
      const degradedCount = streams?.filter((s) => s.stream_status === 'degraded').length ?? 0;
      const totalViewers = streams?.reduce((sum, s) => sum + (s.viewers ?? 0), 0) ?? 0;
      const countries = new Set(streams?.map((s) => s.country) ?? []).size;
      const streamHealth =
        activeStreams > 0
          ? (((activeStreams - degradedCount) / activeStreams) * 100).toFixed(1)
          : '0.0';

      return { activeStreams, totalViewers, streamHealth, countries, degradedCount };
    } catch (err: any) {
      console.error('getDashboardMetrics error:', err.message);
      return null;
    }
  },

  async addStream(streamData: Partial<Stream>): Promise<Stream | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('streams')
        .insert({
          name: streamData.name,
          stream_type: streamData.streamType,
          country: streamData.country,
          flag: streamData.flag ?? '🌍',
          category: streamData.category,
          language: streamData.language ?? 'English',
          stream_url: streamData.streamUrl,
          embed_code: streamData.embedCode,
          description: streamData.description,
          thumbnail: streamData.thumbnail,
          thumbnail_alt: streamData.thumbnailAlt,
          logo_url: streamData.logoUrl,
          stream_status: 'draft',
          viewers: 0,
          uptime: '—',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? mapStream(data) : null;
    } catch (err: any) {
      console.error('addStream error:', err.message);
      return null;
    }
  },

  async deleteStream(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('streams').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (err: any) {
      console.error('deleteStream error:', err.message);
      return false;
    }
  },

  async updateStreamStatus(id: string, status: Stream['streamStatus']): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('streams')
        .update({ stream_status: status, last_verified_at: new Date().toISOString() })
        .eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (err: any) {
      console.error('updateStreamStatus error:', err.message);
      return false;
    }
  },

  async updateStream(id: string, streamData: Partial<Stream>): Promise<Stream | null> {
    const supabase = createClient();
    try {
      const updatePayload: Record<string, any> = {};
      if (streamData.name !== undefined) updatePayload.name = streamData.name;
      if (streamData.streamType !== undefined) updatePayload.stream_type = streamData.streamType;
      if (streamData.country !== undefined) updatePayload.country = streamData.country;
      if (streamData.flag !== undefined) updatePayload.flag = streamData.flag;
      if (streamData.category !== undefined) updatePayload.category = streamData.category;
      if (streamData.language !== undefined) updatePayload.language = streamData.language;
      if (streamData.streamUrl !== undefined) updatePayload.stream_url = streamData.streamUrl;
      if (streamData.embedCode !== undefined) updatePayload.embed_code = streamData.embedCode;
      if (streamData.description !== undefined) updatePayload.description = streamData.description;
      if (streamData.streamStatus !== undefined) updatePayload.stream_status = streamData.streamStatus;
      if (streamData.isFeatured !== undefined) updatePayload.is_featured = streamData.isFeatured;
      if (streamData.genre !== undefined) updatePayload.genre = streamData.genre;
      if (streamData.nowPlaying !== undefined) updatePayload.now_playing = streamData.nowPlaying;
      if (streamData.thumbnail !== undefined) updatePayload.thumbnail = streamData.thumbnail;
      if (streamData.thumbnailAlt !== undefined) updatePayload.thumbnail_alt = streamData.thumbnailAlt;
      if (streamData.logoUrl !== undefined) updatePayload.logo_url = streamData.logoUrl;

      const { data, error } = await supabase
        .from('streams')
        .update(updatePayload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? mapStream(data) : null;
    } catch (err: any) {
      console.error('updateStream error:', err.message);
      return null;
    }
  },

  async getAllEvents(): Promise<LiveEvent[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('live_events')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data?.map(mapEvent) ?? [];
    } catch (err: any) {
      console.error('getAllEvents error:', err.message);
      return [];
    }
  },

  async addEvent(eventData: Partial<LiveEvent>): Promise<LiveEvent | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('live_events')
        .insert({
          title: eventData.title,
          subtitle: eventData.subtitle,
          channel: eventData.channel,
          country: eventData.country,
          flag: eventData.flag ?? '🌍',
          event_time: eventData.eventTime,
          event_status: eventData.eventStatus ?? 'upcoming',
          status_label: eventData.statusLabel,
          viewers: eventData.viewers ?? 0,
          category: eventData.category,
          thumbnail: eventData.thumbnail,
          thumbnail_alt: eventData.thumbnailAlt,
          stream_id: eventData.streamId ?? null,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? mapEvent(data) : null;
    } catch (err: any) {
      console.error('addEvent error:', err.message);
      return null;
    }
  },

  async updateEvent(id: string, eventData: Partial<LiveEvent>): Promise<LiveEvent | null> {
    const supabase = createClient();
    try {
      const updatePayload: Record<string, any> = {};
      if (eventData.title !== undefined) updatePayload.title = eventData.title;
      if (eventData.subtitle !== undefined) updatePayload.subtitle = eventData.subtitle;
      if (eventData.channel !== undefined) updatePayload.channel = eventData.channel;
      if (eventData.country !== undefined) updatePayload.country = eventData.country;
      if (eventData.flag !== undefined) updatePayload.flag = eventData.flag;
      if (eventData.eventTime !== undefined) updatePayload.event_time = eventData.eventTime;
      if (eventData.eventStatus !== undefined) updatePayload.event_status = eventData.eventStatus;
      if (eventData.statusLabel !== undefined) updatePayload.status_label = eventData.statusLabel;
      if (eventData.viewers !== undefined) updatePayload.viewers = eventData.viewers;
      if (eventData.category !== undefined) updatePayload.category = eventData.category;
      if (eventData.streamId !== undefined) updatePayload.stream_id = eventData.streamId || null;
      if (eventData.thumbnail !== undefined) updatePayload.thumbnail = eventData.thumbnail;
      if (eventData.thumbnailAlt !== undefined) updatePayload.thumbnail_alt = eventData.thumbnailAlt;

      const { data, error } = await supabase
        .from('live_events')
        .update(updatePayload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? mapEvent(data) : null;
    } catch (err: any) {
      console.error('updateEvent error:', err.message);
      return null;
    }
  },

  async deleteEvent(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('live_events').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (err: any) {
      console.error('deleteEvent error:', err.message);
      return false;
    }
  },
};
